import React, { useContext, useState } from "react";
import { Card, Row, Col, Tag, Avatar, Button, Space, Divider } from "antd";
import dayjs from "dayjs";
import {
  MessageOutlined,
  // ShareAltOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  generateColor,
  getInitials,
} from "Pages/MyBatches/Channels/ChannelComponents/ChannelComments/CommentPost";
import { ChannelCommentHome } from "Pages/MyBatches/Channels/ChannelComponents/ChannelComments/ChannelCommentHome";
import { IUserInfo } from "utilFunctions/CommonInterfaces/userInterface";
import { UserInfoContext } from "Context/CommonContext";
import Typography from "Components/CommonComponents/Typography";
import "./index.css";
import useMasterContext from "Context/MasterContext";
import UnreadIndicator from "./UnreadIndicator";

interface Post {
  id?: number;
  title: string;
  description: string;
  updatedAt: string;
  status: string;
  subject?: string;
  createdBy?: string;
  creatorName?: string;
  commentCount?: number;
  likesCount?: number;
  postType?: string;
  batchId?: number;
}

interface SinglePostScreenProps {
  post: Post;
  allPosts: Post[];
  handlePostSelect: (post: Post) => void;
  handleEditPost: (post: Post) => void;
  handleDeletePost: (postId: string) => void;
  handleIncrementCommentCount: (postId: string) => void;
  handleDecrementCommentCount: (postId: string) => void;
  hasFullAccess?: boolean;
  onCommentsFetched?: (data: any) => void;
}

export const statusColor = (status?: string) => {
  const map: Record<string, string> = {
    PUBLISHED: "blue",
    DRAFT: "orange",
    SCHEDULED: "green",
  };
  return (status && map[status]) || "blue";
};

const SinglePostScreen: React.FC<SinglePostScreenProps> = ({
  post,
  handlePostSelect,
  handleEditPost,
  handleDeletePost,
  handleIncrementCommentCount,
  handleDecrementCommentCount,
  allPosts,
  hasFullAccess,
  onCommentsFetched,
}) => {
  const { masters } = useMasterContext();
  const { unreadComments } = masters || {};
  const batchId = post.batchId;
  const subject = post.subject;
  const subjectsWiseUnreadComments =
    unreadComments
      ?.find((ele: any) => ele.batchId === batchId)
      ?.subjects.find((comment: any) => comment.subject === subject) || {};
  const postIds = subjectsWiseUnreadComments?.postIds || [];
  const isPostUnread = post.id ? postIds.includes(post.id) : false;
  const userInfo = useContext<IUserInfo>(UserInfoContext);
  const [isCommentSectionVisible, setIsCommentSectionVisible] =
    React.useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const description = post.description || "";
  const isLongDescription = description.length > 200;
  const truncatedDescription = isLongDescription
    ? description.substring(0, 200) + "..."
    : description;

  return (
    <Card className="card-hover-custom card-body-responsive" bordered={false}>
      <Row justify="space-between" align="middle" className="mobile-header-row">
        <Col>
          <Space size="middle" align="center">
            <Avatar
              style={{
                backgroundColor: generateColor(post.creatorName),
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                border: "2px solid #fff",
              }}
              src={post.creatorName}
              size={44}
            >
              {getInitials(post.creatorName)}
            </Avatar>
            <div>
              <Space align="center" size="small" wrap>
                <Typography variant="h6" style={{ fontWeight: 600, margin: 0 }}>
                  {post.creatorName || "Anonymous"}
                </Typography>
                <Tag
                  className="glass-tag"
                  style={{
                    color:
                      statusColor(post.status) === "blue"
                        ? "#1890ff"
                        : statusColor(post.status) === "orange"
                        ? "#faad14"
                        : "#52c41a",
                    backgroundColor:
                      statusColor(post.status) === "blue"
                        ? "#e6f7ff"
                        : statusColor(post.status) === "orange"
                        ? "#fffbe6"
                        : "#f6ffed",
                    border: "none",
                    borderRadius: "4px",
                    fontSize: "10px",
                    padding: "0 6px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {post.status}
                </Tag>
                <UnreadIndicator
                  isUnread={isPostUnread}
                  color={statusColor(post.status)}
                  size={8}
                  title={
                    isPostUnread ? "Unread comments" : "No unread comments"
                  }
                />
              </Space>
              <div style={{ marginTop: -2 }}>
                <Typography className="timestamp-text">
                  {dayjs(post.updatedAt).format("DD MMM, YYYY")}
                </Typography>
              </div>
            </div>
          </Space>
        </Col>
        <Col className="mobile-action-col">
          <Space size="small">
            {post.status != "PUBLISHED" && hasFullAccess && (
              <Button
                type="text"
                shape="circle"
                icon={
                  <EditOutlined
                    style={{ fontSize: "18px", color: "#8c8c8c" }}
                  />
                }
                onClick={() => handleEditPost(post)}
                className="action-btn"
              />
            )}
            {hasFullAccess && (
              <Button
                type="text"
                shape="circle"
                danger
                icon={<DeleteOutlined style={{ fontSize: "18px" }} />}
                onClick={() =>
                  post.id !== undefined && handleDeletePost(post.id.toString())
                }
                className="action-btn"
              />
            )}
          </Space>
        </Col>
      </Row>

      <div style={{ marginTop: 16 }}>
        <Typography variant="h5" className="post-title-text">
          {post.title}
        </Typography>
        <div style={{ marginTop: 8 }}>
          <Typography className="post-description-text">
            {isLongDescription ? (
              <>
                {isExpanded ? description : truncatedDescription}
                <Button
                  type="link"
                  onClick={toggleExpanded}
                  style={{
                    padding: "0 0 0 4px",
                    height: "auto",
                    verticalAlign: "baseline",
                  }}
                >
                  {isExpanded ? "Show less" : "Show more"}
                </Button>
              </>
            ) : (
              description
            )}
          </Typography>
        </div>
      </div>

      <Row justify="space-between" align="middle" style={{ marginTop: 20 }}>
        <Col xs={12} sm={12}>
          {post.postType == "POST" &&
            post.status == "PUBLISHED" &&
            (post.commentCount ?? 0) > 0 && (
              <div
                className="comment-icon-wrap comment-wrap-mobile"
                onClick={() =>
                  setIsCommentSectionVisible(!isCommentSectionVisible)
                }
                style={{ cursor: "pointer", display: "inline-flex" }}
              >
                <MessageOutlined
                  style={{ color: "#1890ff", fontSize: "18px" }}
                />
                <span
                  style={{ fontWeight: 500, fontSize: "14px", marginLeft: 8 }}
                  className="filterSpan"
                >
                  {post.commentCount ?? 0}
                </span>
              </div>
            )}
        </Col>
        <Col xs={12} sm={12} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handlePostSelect(post)}
          >
            View
          </Button>
        </Col>
      </Row>

      {post?.postType == "POST" &&
        post?.status == "PUBLISHED" &&
        isCommentSectionVisible && (
          <div style={{ animation: "fadeIn 0.3s ease-in-out" }}>
            <Divider style={{ margin: "24px 0 0 0" }} />
            <ChannelCommentHome
              selectedPost={post}
              allPosts={allPosts}
              userInfoid={userInfo.id}
              handleIncrementCommentCount={handleIncrementCommentCount}
              handleDecrementCommentCount={handleDecrementCommentCount}
              onCommentsFetched={onCommentsFetched}
            />
          </div>
        )}
    </Card>
  );
};

export default SinglePostScreen;
